import React from 'react';
import { CheckCircle, Smartphone, ArrowRight, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { buildScheme } from '../../DeepLinkUtils';
import { Button } from '../../../components/ui/Button';
import { detectPlatform } from '../../DeepLinkUtils';
import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL, MOBILE_APP_PAUSED } from '../../../constants/appStore';

export function CompleteStep() {
  const navigate = useNavigate();
  const platform = detectPlatform();

  const handleGoToApp = () => {
    // Build deep link to login page
    const deepLink = buildScheme('/login');

    // Try to open app, fallback to store if not installed
    try {
      window.location.href = deepLink;

      // If still on page after 2 seconds, redirect to store
      setTimeout(() => {
        if (document.hasFocus()) {
          const storeUrl = platform === 'ios' ? IOS_APP_STORE_URL : platform === 'android' ? ANDROID_PLAY_STORE_URL : IOS_APP_STORE_URL;
          window.open(storeUrl, '_blank');
        }
      }, 2000);
    } catch {
      const storeUrl = platform === 'ios' ? IOS_APP_STORE_URL : platform === 'android' ? ANDROID_PLAY_STORE_URL : IOS_APP_STORE_URL;
      window.open(storeUrl, '_blank');
    }
  };

  const handleContinueOnWeb = () => {
    navigate('/dashboard');
  };

  // Phase 1: mobile is paused — funnel students straight into the web dashboard
  // with no install prompt or app-store CTAs.
  if (MOBILE_APP_PAUSED) {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-8 border border-[#504A4A]">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={48} className="text-green-500" />
          </div>
          <h2 className="text-3xl font-bold text-[#EBFFEE]">You&apos;re All Set!</h2>
          <p className="text-[#A9A3A3] mt-2">Your profile is complete</p>
        </div>

        <div className="bg-[#363232] rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-[#EBFFEE] mb-3">What&apos;s next</h3>
          <ul className="space-y-2 text-sm text-[#A9A3A3]">
            <li className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              View your class schedule
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              Manage your membership
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              Connect with your gym community
            </li>
          </ul>
        </div>

        <Button
          onClick={handleContinueOnWeb}
          variant="red"
          size="lg"
          className="w-full font-semibold flex items-center justify-center gap-2"
        >
          <LogIn size={20} />
          Continue to Dashboard
          <ArrowRight size={18} />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-8 border border-[#504A4A]">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={48} className="text-green-500" />
        </div>
        <h2 className="text-3xl font-bold text-[#EBFFEE]">You&apos;re All Set!</h2>
        <p className="text-[#A9A3A3] mt-2">Your profile is complete</p>
      </div>

      <div className="bg-[#363232] rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-[#EBFFEE] mb-3 flex items-center gap-2">
          <Smartphone size={20} className="text-rollops-accent" />
          Next Steps
        </h3>
        <p className="text-sm text-[#A9A3A3] mb-4">
          Download the RollOps mobile app to access your gym membership, view schedules, and connect with your community.
        </p>
        <ul className="space-y-2 text-sm text-[#A9A3A3]">
          <li className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-500" />
            View your class schedule
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-500" />
            Manage your membership
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-500" />
            Connect with your gym community
          </li>
        </ul>
      </div>

      <div className="space-y-3">
        <Button
          onClick={handleGoToApp}
          variant="red"
          size="lg"
          className="w-full font-semibold flex items-center justify-center gap-2"
        >
          <Smartphone size={20} />
          Open RollOps App
          <ArrowRight size={18} />
        </Button>

        <div className="flex flex-col sm:flex-row gap-3">
          {platform === 'ios' ? (
            <Button
              onClick={() => window.open(IOS_APP_STORE_URL, '_blank')}
              variant="outline"
              className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-3"
            >
              Download for iOS
            </Button>
          ) : platform === 'android' ? (
            <Button
              onClick={() => window.open(ANDROID_PLAY_STORE_URL, '_blank')}
              variant="outline"
              className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-3"
            >
              Download for Android
            </Button>
          ) : (
            <>
              <Button
                onClick={() => window.open(IOS_APP_STORE_URL, '_blank')}
                variant="outline"
                className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-3"
              >
                Download for iOS
              </Button>
              <Button
                onClick={() => window.open(ANDROID_PLAY_STORE_URL, '_blank')}
                variant="outline"
                className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-3"
              >
                Download for Android
              </Button>
            </>
          )}
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#504A4A]" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-[#221F1F] text-[#A9A3A3]">Or</span>
          </div>
        </div>

        <Button
          onClick={handleContinueOnWeb}
          variant="outline"
          size="lg"
          className="w-full font-semibold flex items-center justify-center gap-2 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent"
        >
          <LogIn size={20} />
          Continue on Web
        </Button>
      </div>
    </div>
  );
}
